import { ReactElement, useCallback, useMemo } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { Button } from '@shared/components/Button';
import { Modal } from '@shared/components/Modal';
import { View } from '@shared/components/View';
import { grid } from '@shared/styles';
import { useBoolean, useExecuteOnTrigger, useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { Route, useNavStack } from '@shared/util/navigation';
import { MBMultiSelect } from './MBMultiSelect';
import { FIXED_WIDTH } from './constants';
import { MBMultiSelectEdit } from './edit';
import { MBMultiSelectModalProps } from './modalProps';
import styles from './modal.module.css';

const initialRoutes: Route[] = [{ name: 'select' }];

export function MBMultiSelectModal<T>({
  children,
  label,
  onOpen,
  onClose,
  itemsEditable = false,
  editMode,
  itemLabel,
  itemLabelPlural,
  onArchive,
  onUnarchive,
  onChangeLabel,
  trigger,
  anchorName,
  testID,
  ...multiSelectProps
}: MBMultiSelectModalProps<T>): ReactElement {
  const isDesktop = useIsDesktop();
  const [visible, showModal, hideModal] = useBoolean(false);
  const nav = useNavStack(initialRoutes);

  const handleOpen = useCallback(() => {
    showModal();
    onOpen?.();
  }, [onOpen, showModal]);
  const handleClose = useCallback(() => {
    hideModal();
    nav.reset(initialRoutes);
    onClose?.();
  }, [onClose, nav, hideModal]);

  useExecuteOnTrigger(trigger, handleOpen);

  const title = useMemo(() => {
    if (nav.currentRoute?.name === 'manage') {
      return i18n('multiSelect.manage', { item: itemLabelPlural ?? '' });
    }
    if (nav.currentRoute?.name === 'edit') {
      return i18n('multiSelect.edit', { item: itemLabel ?? '' });
    }
    return label ?? '';
  }, [nav.currentRoute, itemLabelPlural, itemLabel, label]);

  const handleGearPress = useMemo(() => {
    if (nav.currentRoute?.name === 'select') {
      return () => nav.push({ name: 'manage' });
    }
    return undefined;
  }, [nav]);

  const handleBack = useMemo(() => {
    if (nav.currentRoute?.name === 'manage' || nav.currentRoute?.name === 'edit') {
      return () => {
        nav.pop();
      };
    }
    return undefined;
  }, [nav]);

  const handleEdit = useCallback(
    (value: T) => {
      nav.push({ name: 'edit', params: { value } });
    },
    [nav],
  );

  const handleArchive = useCallback(
    async (value: T) => {
      await onArchive?.(value);
      handleBack?.();
    },
    [handleBack, onArchive],
  );

  const handleUnarchive = useCallback(
    async (value: T) => {
      await onUnarchive?.(value);
      handleBack?.();
    },
    [handleBack, onUnarchive],
  );

  const handleChangeLabel = useCallback(
    async (updatedValue: T, updatedLabel: string) => {
      await onChangeLabel?.(updatedValue, updatedLabel);
      handleBack?.();
    },
    [handleBack, onChangeLabel],
  );
  const showTitleBar = !isDesktop || itemsEditable;
  const isSelectRoute = nav.currentRoute?.name === 'select';
  const gearButton = itemsEditable && (
    <Button variant="ghost" icon="gear" iconOnly iconSize={grid(4)} onClick={handleGearPress} />
  );

  const fixedWidth = isDesktop ? FIXED_WIDTH : undefined;
  const archivedValues = multiSelectProps.archivedValues;
  const paramValue = nav.currentRoute?.params?.value as T;
  const isArchived = archivedValues?.includes(paramValue) ?? false;

  const isEdit = nav.currentRoute?.name === 'edit';

  return (
    <>
      <View className={styles.multiSelectButton}>
        {/* Sibling overlay instead of a wrapping <button>: the field summary
            children can contain buttons (e.g. ClearButton), which are invalid
            DOM inside a button element. */}
        <button
          type="button"
          className={styles.multiSelectTrigger}
          onClick={handleOpen}
          aria-label={label}
        />
        {children}
      </View>
      {visible && (
        <AnchoredSheet
          anchorName={anchorName}
          onClose={handleClose}
          testID={testID}
          aria-label={label}
          className={styles.multiSelectSheet}
          style={fixedWidth}
        >
          {showTitleBar && (
            <Modal.TitleBar
              title={isDesktop && itemsEditable ? undefined : title}
              startContent={
                <>
                  {!isSelectRoute && handleBack && <Modal.BackButton onClick={handleBack} />}
                  {isSelectRoute && !isDesktop && gearButton}
                </>
              }
              endContent={
                <>
                  {isSelectRoute && isDesktop && gearButton}
                  {!isDesktop && (!itemsEditable || isSelectRoute) && (
                    <Modal.CloseButton onClick={handleClose} />
                  )}
                </>
              }
            />
          )}
          {isEdit && (
            <MBMultiSelectEdit
              {...multiSelectProps}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onChangeLabel={handleChangeLabel}
              value={nav.currentRoute?.params?.value}
              isArchived={isArchived}
            />
          )}
          {!isEdit && (
            <MBMultiSelect
              {...multiSelectProps}
              onClose={handleClose}
              editMode={nav.currentRoute?.name === 'manage'}
              onEdit={handleEdit}
            />
          )}
        </AnchoredSheet>
      )}
    </>
  );
}
